import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Crest } from '@/components/Brand';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('pending'); // pending | paid | expired | error
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const polledRef = useRef(false);

  useEffect(() => {
    if (polledRef.current) return;
    polledRef.current = true;
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      if (n >= 8) { setStatus('error'); return; }
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        setAttempts(n + 1);
        if (data.payment_status === 'paid') { setStatus('paid'); return; }
        if (data.status === 'expired') { setStatus('expired'); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-trjlight flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-md w-full text-center">
        <Crest size="lg" className="mx-auto" />
        {status === 'pending' && (
          <>
            <Clock className="h-12 w-12 text-gold mx-auto mt-6" />
            <h1 className="font-display font-semibold text-2xl text-navy mt-3">Finalizing your payment…</h1>
            <p className="text-sm text-slate-600 mt-2">Don't refresh. We're confirming with Stripe ({attempts}/8).</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="h-12 w-12 text-trjgreen mx-auto mt-6" />
            <h1 className="font-display font-semibold text-2xl text-navy mt-3">Payment confirmed</h1>
            <p className="text-sm text-slate-600 mt-2">You're enrolled. A confirmation email is on the way.</p>
            <Button onClick={() => navigate('/dashboard')} className="btn-navy mt-6" data-testid="go-dashboard">Go to dashboard</Button>
          </>
        )}
        {status === 'expired' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mt-6" />
            <h1 className="font-display font-semibold text-2xl text-navy mt-3">Checkout expired</h1>
            <p className="text-sm text-slate-600 mt-2">Please try again from the program page.</p>
            <Link to="/programs" className="inline-block mt-4 text-navy font-semibold hover:text-gold">Browse programs</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mt-6" />
            <h1 className="font-display font-semibold text-2xl text-navy mt-3">Could not verify payment</h1>
            <p className="text-sm text-slate-600 mt-2">Check your email for confirmation, or contact support@trjacademy.com.</p>
            <Link to="/dashboard" className="inline-block mt-4 text-navy font-semibold hover:text-gold">Open dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
